import { useEffect } from "react";
import { Link } from "react-router-dom";

// Privacy Policy — public marketing page. Includes the TCPA/A2P-mandated
// mobile-information-sharing statement required for our 10DLC campaign
// approval + carrier compliance. Text is deliberately verbatim from
// the compliance draft the owner provided (2026-08-23) so we don't drift
// out of alignment with what SignalWire submitted to The Campaign
// Registry. Do NOT paraphrase the highlighted paragraph without owner
// sign-off — it's what tells T-Mobile / AT&T / Verizon that we're a
// legit sender.
export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy · FlipPilot";
  }, []);

  const updated = "August 23, 2026";
  return (
    <div className="mkt-container py-16 md:py-24" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto">
        <span
          className="inline-block text-xs uppercase tracking-widest px-3 py-1 rounded-full"
          style={{ background: "var(--mkt-surface-2)", color: "var(--mkt-accent-dark)" }}
        >
          Legal
        </span>
        <h1 className="mkt-hero-title mt-6 leading-tight">Privacy Policy</h1>
        <p className="mt-4 text-sm text-[var(--mkt-text-muted)]">
          Last updated: {updated}
        </p>

        <section className="mt-10 space-y-6 text-[15px] leading-relaxed text-[var(--mkt-text)]">
          <p>
            Flip Pilot LLC (&ldquo;<b>FlipPilot</b>&rdquo;, &ldquo;we&rdquo;,
            &ldquo;our&rdquo;, or &ldquo;us&rdquo;) respects your privacy and is
            committed to protecting the personal information you share with us.
            This Privacy Policy explains what information we collect, how we
            use it, how we protect it, and the choices you have regarding your
            information when you visit{" "}
            <a href="https://flippilot.us" className="underline">flippilot.us</a>{" "}
            or interact with our services.
          </p>

          {/* TCPA / A2P 10DLC statement — verbatim, owner-approved. */}
          <div
            className="rounded-lg border-2 p-5 my-2"
            style={{ background: "var(--mkt-surface)", borderColor: "var(--mkt-accent-dark)" }}
            data-testid="privacy-mobile-info-statement"
          >
            <div className="text-xs uppercase tracking-widest font-bold mb-2"
                 style={{ color: "var(--mkt-accent-dark)" }}>
              Mobile Information &amp; Consent Statement
            </div>
            <p className="text-[15px] font-semibold">
              &ldquo;No mobile information will be shared with third
              parties/affiliates for marketing/promotional purposes. All other
              categories exclude text messaging originator opt-in data and
              consent; this information will not be shared with any third
              parties.&rdquo;
            </p>
          </div>

          <h2 className="text-xl font-semibold mt-10">Information We Collect</h2>
          <p>
            We collect information you provide directly, including your name,
            phone number, email address, mailing address, and details about
            your property when you request a cash offer or contact us through
            our website. When you interact with our website we may also
            automatically collect certain technical data such as your IP
            address, browser type, device type, and pages viewed.
          </p>

          <h2 className="text-xl font-semibold mt-10">How We Use Your Information</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>To evaluate and prepare a cash offer for your property.</li>
            <li>To respond to your inquiries by phone, email, or text message.</li>
            <li>To send you follow-up communications about your submission.</li>
            <li>To improve our services, website, and customer experience.</li>
            <li>To comply with applicable laws and regulations.</li>
          </ul>

          <h2 className="text-xl font-semibold mt-10">Text Messaging &amp; Consent</h2>
          <p>
            By providing your phone number and opting in, you consent to receive
            text messages from FlipPilot related to your inquiry, offer status,
            and follow-up communications. Message and data rates may apply.
            Message frequency varies. Reply <b>STOP</b> at any time to
            unsubscribe or <b>HELP</b> for assistance. Your mobile opt-in data
            and consent information will <b>never</b> be shared with third
            parties for any purpose, including marketing and promotional
            purposes.
          </p>

          <h2 className="text-xl font-semibold mt-10">How We Protect Your Information</h2>
          <p>
            We implement administrative, technical, and physical safeguards
            designed to protect your information from unauthorized access,
            disclosure, alteration, or destruction. Access to personally
            identifiable information is restricted to authorized personnel who
            need it to perform their job responsibilities.
          </p>

          <h2 className="text-xl font-semibold mt-10">Third Parties</h2>
          <p>
            We do not sell, rent, or trade your personal information.
            We may share your information only with trusted service providers
            (such as our telephony provider, hosting provider, or CRM) that
            help us operate our business, and only to the extent necessary to
            deliver our services to you. These providers are contractually
            required to protect your information and use it only for the
            services they provide to us.
          </p>

          <h2 className="text-xl font-semibold mt-10">Your Choices</h2>
          <p>
            You may request to access, correct, or delete the personal
            information we hold about you at any time by contacting us using
            the details below. You may also opt out of text messages by
            replying <b>STOP</b> to any message you receive from us.
          </p>

          <h2 className="text-xl font-semibold mt-10">Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. When we do,
            we will revise the &ldquo;Last updated&rdquo; date at the top of
            this page. Continued use of our website or services after any
            change constitutes acceptance of the updated policy.
          </p>

          <h2 className="text-xl font-semibold mt-10">Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or how we
            handle your personal information, please contact us:
          </p>
          <ul className="list-none pl-0 space-y-1">
            <li>
              <b>Flip Pilot LLC</b>
            </li>
            <li>
              Phone:{" "}
              <a
                href="tel:+12394808400"
                className="underline hover:text-[var(--mkt-accent-dark)]"
                data-testid="privacy-contact-phone"
              >
                (239) 480-8400
              </a>
            </li>
            <li>
              Email:{" "}
              <a
                href="mailto:sales@flippilot.us"
                className="underline hover:text-[var(--mkt-accent-dark)]"
              >
                sales@flippilot.us
              </a>
            </li>
          </ul>

          <div className="mt-16 pt-6 border-t border-[var(--mkt-line)] flex items-center justify-between text-sm">
            <Link
              to="/contact"
              className="mkt-btn mkt-btn-primary"
              data-testid="privacy-back-to-contact"
            >
              Contact Us
            </Link>
            <Link to="/" className="text-[var(--mkt-text-muted)] hover:text-[var(--mkt-black)]">
              &larr; Back to home
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
